import { Amplify } from 'aws-amplify'
import { type AuthUser, getCurrentUser } from 'aws-amplify/auth'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'

export const webClientId = import.meta.env.VITE_AWS_USER_POOLS_WEB_CLIENT_ID

/**
 * Cognito で認証されていない場合のエラー
 */
export const USER_UNAUTHENTICATED_EXCEPTION = 'UserUnAuthenticatedException'

/**
 * Cognito で認証されていない場合のエラー
 */
export const NOT_AUTHORIZED_EXCEPTION = 'NotAuthorizedException'

const authCognitoWeb = {
  userPoolClientId: webClientId,
  userPoolId: import.meta.env.VITE_AWS_USER_POOLS_ID,
}

const apiGraphql = {
  endpoint: import.meta.env.VITE_AWS_APPSYNC_GRAPHQL_ENDPOINT,
  region: import.meta.env.VITE_AWS_COGNITO_REGION,
}

/**
 * エラーが認証エラーかどうか
 */
export function isUnAuthiruxedError(error: unknown) {
  return error instanceof Error
    ? error.name === USER_UNAUTHENTICATED_EXCEPTION || error.name === NOT_AUTHORIZED_EXCEPTION
    : false
}

/**
 * fetchUserAttributes のキャッシュ
 *
 * - `undefined`: 未認証
 * - `null`: 未取得
 */
let cacheFetchUserAttributes: AuthUser | undefined | null = null

/**
 * Auth.fetchUserAttributes
 */
export async function fetchUserAttributes() {
  enableAuthentication()

  // logger.verbose('fetchUserAttributes', { cacheFetchUserAttributes })

  if (cacheFetchUserAttributes !== null) return cacheFetchUserAttributes

  const user = await getCurrentUser().catch((error) => {
    throw error
  })

  if (!user || !user.userId) return null

  cacheFetchUserAttributes = user

  return user
}

/**
 * Auth.fetchUserAttributes のキャッシュをクリアします
 */
export function clearUserAttributes() {
  cacheFetchUserAttributes = null
}

export function enableAuthentication() {
  // AppSync は今回の移行対象外で、環境によっては未設定のことがある。
  // endpoint が空のまま API.GraphQL を渡すと Amplify.configure が失敗し、
  // ログインごと動かなくなるので、設定がある場合だけ含める。
  Amplify.configure({
    Auth: { Cognito: { ...authCognitoWeb } },
    ...(apiGraphql.endpoint ? { API: { GraphQL: { ...apiGraphql, defaultAuthMode: 'userPool' as const } } } : {}),
  })

  cognitoUserPoolsTokenProvider.setAuthConfig({
    Cognito: authCognitoWeb,
  })
}

enableAuthentication()

export function disableAuthentication() {
  Amplify.configure({
    Auth: {
      Cognito: {
        ...authCognitoWeb,
        // IAM のゲストロールでの認証を許可するためには、identityPoolId が必要です
        identityPoolId: import.meta.env.VITE_AWS_IDENTITY_POOL_ID,
        allowGuestAccess: true,
      },
    },
    ...(apiGraphql.endpoint ? { API: { GraphQL: { ...apiGraphql, defaultAuthMode: 'iam' as const } } } : {}),
  })
}

/**
 * 認証に必要なローカルストレージをクリアします
 *
 * 同様のロジックが以下のファイルにも存在します
 * これは Vuejs（Laravel） から React のセッションをログアウトするために使用されています
 *
 * canaly/resources/views/aws/logout.blade.php
 */
export function clearAuthentication() {
  if (window['localStorage']) {
    const re = `^(Cognito|com.amplify.Cognito.)`
    const cognitRe = new RegExp(re.replace(/\./g, '\\.'))
    const len = localStorage.length
    for (let i = 0; i < len; i++) {
      const key = localStorage.key(i)
      if (key && cognitRe.test(key)) localStorage.removeItem(key)
    }
  }
}
